'use client'

import { Zap } from 'lucide-react'
import { HeroIllustration } from './hero-illustration'
import { HomeSearchEntry } from './home-search-entry'
import { useI18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'

// LandingHero — the illustrated hero with headline + sub + the real search
// (query chips, no FilterTags). Extracted from WelcomeGate 2026-07-08
// (user-directed): the signed-in home renders the SAME hero, so logging in
// changes the content below the fold, not the search experience.
//
// Layout notes preserved from the gate:
// - overflow-hidden lives on the illustration wrapper, NOT the hero
//   container — SearchInput's city/type dropdowns are inline absolute (z-50)
//   and must be free to hang below the hero edge.
// - the content block is bottom-anchored; anything that grows in-flow would
//   push the stack upward (that's why the examples panel floats).
export function LandingHero({
  fill = false,
  children,
}: {
  /** Fill the viewport below the 90px header and center the content block
      vertically — for pages with nothing below the fold (signed-in home).
      Default keeps the gate's fixed heights + bottom-anchored content so
      the guest sections flow right under the rooftops. */
  fill?: boolean
  /** Overlay content positioned by the caller (e.g. the terms line),
      rendered inside the hero's relative container. */
  children?: React.ReactNode
}) {
  const { t } = useI18n()

  return (
    // fill sizing/positioning is inline style, not arbitrary Tailwind
    // classes — the running dev server's JIT wasn't emitting some of the
    // new arbitrary utilities (top-[44%], min-h-[560px]), and inline styles
    // can't silently miss.
    <div
      className={cn('relative', !fill && 'h-[440px] md:h-[500px] lg:h-[620px]')}
      style={fill ? { height: 'calc(100dvh - 90px)', minHeight: 560 } : undefined}
    >
      <div className="absolute inset-0 overflow-hidden">
        <HeroIllustration />

        {/* Veil for legibility (fades into the page bg at the bottom).
            --h-veil is theme-set next to the illustration palette: light
            scrim over the morning scene, dark over dusk/hacker. */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgb(var(--h-veil) / 0.42), rgb(var(--h-veil) / 0.12) 32%, rgb(var(--h-veil) / 0.35) 62%, rgb(var(--gate-bg)))',
          }}
        />
      </div>

      {/* Lead hook + headline + sub + the real search.
          Centered desktop column (max-w-1120), NOT the logo's full-width
          rail — user reverted the flush-left alignment 2026-07-08. */}
      {/* 44% (not 50%): optical centering — dead center reads as sitting
          low; nudging the block up feels balanced and leaves the rooftops
          room to breathe. */}
      <div
        className={cn(
          'absolute inset-x-0 mx-auto w-full max-w-[680px] px-[22px] lg:max-w-[1120px]',
          !fill && 'bottom-[26px] lg:bottom-12'
        )}
        style={fill ? { top: '44%', transform: 'translateY(-50%)' } : undefined}
      >
        <div className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-[rgb(var(--h-ink)/0.3)] bg-[rgb(var(--h-ink)/0.15)] py-1 pl-2 pr-[11px] backdrop-blur-sm">
          <Zap className="h-[15px] w-[15px] text-[rgb(var(--h-ink))]" />
          <span className="text-xs font-bold tracking-[0.2px] text-[rgb(var(--h-ink))]">
            {t('gate.hook', 'Instant alerts')}
          </span>
        </div>
        <h1 className="font-serif italic text-[42px] leading-[42px] tracking-[-1.5px] text-[rgb(var(--h-ink))] [text-shadow:var(--h-ink-shadow)] md:text-[52px] md:leading-[50px] lg:text-[72px] lg:leading-[68px]">
          {t('gate.headline1', 'Be first')}
          <br />
          {t('gate.headline2', 'through the door.')}
        </h1>
        {/* One-line sub (reuses intro1/introStrong keys — intro2 retired). */}
        <p className="mt-3.5 max-w-xl text-[15px] leading-[22px] text-[rgb(var(--h-ink)/0.85)] [text-shadow:var(--h-ink-shadow)] lg:text-[17px] lg:leading-[26px]">
          {t('gate.intro1', 'The best flats in Czechia let in ')}
          <strong className="font-bold text-[rgb(var(--h-ink))]">
            {t('gate.introStrong', 'within hours')}
          </strong>
          .
        </p>
        <div className="mt-5 max-w-3xl lg:mt-7">
          <HomeSearchEntry showFilterTags={false} showQueryChips />
        </div>
      </div>

      {children}
    </div>
  )
}
